const FRENCH_LOCALE = /^fr(?:-|$)/i;

/**
 * Formats USD with locale-specific digits and separators. French locales use a leading narrow
 * dollar symbol without the disambiguating `US` suffix.
 */
export function formatUsdParts(
  value: number,
  locale: string,
  options: Pick<Intl.NumberFormatOptions, 'minimumFractionDigits' | 'maximumFractionDigits'> = {}
) {
  const isFrench = FRENCH_LOCALE.test(locale);
  const parts = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'USD',
    currencyDisplay: isFrench ? 'narrowSymbol' : 'symbol',
    ...options,
  }).formatToParts(value);
  if (!isFrench) return parts;

  const currencyIndex = parts.findIndex((part) => part.type === 'currency');
  if (currencyIndex < 0) return parts;
  const currency = parts[currencyIndex];
  if (!currency) return parts;
  const numberParts = parts.filter(
    (part, index) =>
      index !== currencyIndex && !(index === currencyIndex - 1 && part.type === 'literal')
  );
  const signLength =
    numberParts[0]?.type === 'minusSign' || numberParts[0]?.type === 'plusSign' ? 1 : 0;
  return [...numberParts.slice(0, signLength), currency, ...numberParts.slice(signLength)];
}

/** Formats USD using the display rules from {@link formatUsdParts}. */
export function formatUsd(
  value: number,
  locale: string,
  options: Pick<Intl.NumberFormatOptions, 'minimumFractionDigits' | 'maximumFractionDigits'> = {}
) {
  return formatUsdParts(value, locale, options)
    .map((part) => part.value)
    .join('');
}
